import { Fragment, useEffect, useRef, useState } from 'react';
import { isSetOp, type Clause, type ClauseOp, type MasterList } from '@kkp-fraud/shared';
import {
  FIELD_GROUPS,
  FIELD_KINDS,
  OPS,
  customFieldOf,
  fieldHint,
  isStandardField,
  kindOf,
  opLabel,
  defaultValueFor,
  listToInput,
  masterListsFor,
  opsFor,
  parseListInput,
  valueInputKind,
  type FieldName,
} from '../fields';
import { formatClauses, num } from '../format';
import { t } from '../i18n';
import { SCREENS } from '../navigation';
import { useCustomFields } from '../hooks/useCustomFields';
import type { SourceId } from '../sources';
import { SourceButtons } from './SourceButtons';

/**
 * Rows of [AND/OR] [field] [operator] [value]. The value editor changes shape with the
 * operator — a list for in/nin, a pair for between, a checkbox for a true/false field, a
 * choice of Master list for in_set/not_in_set (R7) — so an analyst cannot type a string into
 * a numeric field and wonder why nothing fires.
 */

/** Master Management, opened beside the editor so the rule being edited is not lost. */
const MASTERS_PATH = SCREENS.find((s) => s.id === 'masters')?.path ?? '/masters';

export interface ClauseBuilderProps {
  clauses: Clause[];
  onChange: (next: Clause[]) => void;
  /** Pattern filters are always ANDed by the engine; hide the logic column there. */
  showLogic?: boolean;
  /** The heading above the chain; omitted means no heading (the rule editor's action rows). */
  label?: string;
  /** Used in aria-labels/test ids so two builders on one screen stay distinguishable. */
  idPrefix?: string;
  /**
   * Restricts the field dropdown — a field no selected source sends is not offered at all
   * (PLAN-v2 §1.3: unavailable choices are not shown, not shown-and-disabled). Omitted
   * means every field, which is what the standalone builder tests exercise.
   */
  allowedFields?: readonly FieldName[];
  /**
   * Per-row source selection. Omitted means no source buttons — the builder stays usable on
   * its own, and only the rule editor wires the registry in.
   */
  rowSources?: readonly (readonly SourceId[])[];
  onRowSourcesChange?: (index: number, next: SourceId[]) => void;
  /**
   * The rule editor prints the same expression under its own heading a few lines below, so
   * it turns this one off rather than showing the reader the same string twice.
   */
  showPreview?: boolean;
  /**
   * Nothing changes at all — an account that may only read (a Viewer). Every control is
   * shown disabled, the value editors too, and there is no "+ condition" and no ✕.
   */
  readOnly?: boolean;
  /**
   * The Master lists an in_set / not_in_set row may choose from (`useMasterLists`). `null` or
   * omitted means not known: the row still shows the list it names, and the server decides
   * whether that list exists.
   */
  masters?: readonly MasterList[] | null;
  /**
   * What the engine said about each condition, by 0-based row — the rule editor maps the
   * engine's problems onto this chain (Wave 2-0: the builder checks nothing itself).
   */
  notes?: ReadonlyMap<number, readonly string[]>;
}

/**
 * A comma-separated list keeps its own text. Rendering straight from the parsed array
 * would delete the separator the moment it is typed — "5411," parses to [5411], which
 * renders as "5411", and the list can never grow past one entry.
 */
function ListInput({
  value,
  kind,
  onChange,
  ...rest
}: {
  value: unknown;
  kind: 'string' | 'number' | 'boolean';
  onChange: (next: unknown[]) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  const [text, setText] = useState(() => listToInput(value));
  const emitted = useRef(JSON.stringify(Array.isArray(value) ? value : []));

  useEffect(() => {
    const next = JSON.stringify(Array.isArray(value) ? value : []);
    // Only adopt a value this input did not produce — a field change, an undo, a reload.
    if (next !== emitted.current) {
      emitted.current = next;
      setText(listToInput(value));
    }
  }, [value]);

  return (
    <input
      {...rest}
      type="text"
      value={text}
      onChange={(e) => {
        const raw = e.target.value;
        setText(raw);
        const parsed = parseListInput(raw, kind);
        emitted.current = JSON.stringify(parsed);
        onChange(parsed);
      }}
    />
  );
}

export function ClauseBuilder({
  clauses,
  onChange,
  showLogic = true,
  label,
  idPrefix = 'clause',
  allowedFields,
  rowSources,
  onRowSourcesChange,
  showPreview = true,
  readOnly = false,
  masters = null,
  notes,
}: ClauseBuilderProps) {
  // Wave 6 — the set's custom fields, offered under their own group after the standard ones.
  const custom = useCustomFields();
  const update = (index: number, patch: Partial<Clause>) => {
    const next = clauses.map((c, i) => (i === index ? { ...c, ...patch } : c));
    onChange(next);
  };

  /**
   * A fresh value for `field`/`op`. For a set op, the first list that fits the field (or `''`
   * while none does, or the lists are not known) — a set clause never holds an array or a number.
   */
  const freshValue = (field: FieldName, op: ClauseOp): unknown =>
    isSetOp(op) ? (masters ? (masterListsFor(field, masters)[0]?.id ?? '') : '') : defaultValueFor(field, op);

  const changeField = (index: number, field: FieldName) => {
    const current = clauses[index];
    if (!current) return;
    const allowed = opsFor(kindOf(field) ?? 'string');
    const op: ClauseOp = allowed.includes(current.op) ? current.op : (allowed[0] as ClauseOp);
    update(index, { field, op, value: freshValue(field, op) });
  };

  const changeOp = (index: number, op: ClauseOp) => {
    const current = clauses[index];
    if (!current) return;
    const field = current.field as FieldName;
    const beforeKind = valueInputKind(field, current.op);
    const afterKind = valueInputKind(field, op);
    // Keep the typed value when the editor shape does not change; otherwise reset it to a
    // type-correct default so the row is never briefly invalid in a way the analyst caused.
    // in_set ⇄ not_in_set keeps the chosen list: both editors are the same list choice.
    update(index, { op, ...(beforeKind === afterKind ? {} : { value: freshValue(field, op) }) });
  };

  const add = () => onChange([...clauses, { logic: 'AND', field: 'amount_thb', op: 'gt', value: 0 }]);
  const remove = (index: number) => onChange(clauses.filter((_, i) => i !== index));

  return (
    <div>
      {label && (
        <div className="row" style={{ marginBottom: 6 }}>
          <span className="note" style={{ margin: 0 }}>
            {label}
          </span>
        </div>
      )}

      {clauses.length === 0 && <div className="empty small">{t('clause.empty.label')}</div>}

      {/*
        The sentence chain from the mockup: beads left to right read as one sentence —
        [field] [comparison] [value], joined by AND/OR, exactly the shape of the rule-intake
        slide the client already understood. Colour carries each bead's ROLE, not decoration.
        Every bead here is a real control; the slide's were labels.
      */}
      <div className={readOnly ? 'chain readonly' : 'chain'}>
        {clauses.map((clause, i) => {
          const field = clause.field as FieldName;
          const kind = kindOf(field) ?? 'string';
          const inputKind = valueInputKind(field, clause.op);
          const rowId = `${idPrefix}-${i}`;

          return (
            <Fragment key={rowId}>
              {i > 0 &&
                (showLogic ? (
                  <select
                    className="pill logic"
                    aria-label={t('clause.row.join', { n: i + 1 })}
                    disabled={readOnly}
                    value={clause.logic ?? 'AND'}
                    onChange={(e) => update(i, { logic: e.target.value as 'AND' | 'OR' })}
                  >
                    <option value="AND">AND</option>
                    <option value="OR">OR</option>
                  </select>
                ) : (
                  // 'AND' is the engine's own clause value, not prose — it stays verbatim.
                  <span className="pill logic">AND</span>
                ))}

              <span className="cl" data-testid={rowId}>
                <span className="pillwrap">
                  <span className="pillcap">
                    {t('form.cap.data')}
                    {rowSources && onRowSourcesChange && (
                      <SourceButtons
                        field={field}
                        value={rowSources[i] ?? []}
                        rowLabel={t('clause.row.label', { n: i + 1 })}
                        disabled={readOnly}
                        onChange={(next) => onRowSourcesChange(i, next)}
                      />
                    )}
                  </span>
                  <select
                    className="pill field"
                    aria-label={t('clause.row.field', { n: i + 1 })}
                    disabled={readOnly}
                    title={fieldHint(field) ?? customFieldOf(field)?.description}
                    value={field}
                    onChange={(e) => changeField(i, e.target.value as FieldName)}
                  >
                    {FIELD_GROUPS.map((g) => {
                      // A group whose every field is unavailable disappears with its fields —
                      // an empty optgroup label is the same ghost in a different shape.
                      // The row's own field is always offered: a select whose value is not among its
                      // options renders blank, and the analyst would see an empty condition.
                      const offered = allowedFields ? g.fields.filter((f) => f === field || allowedFields.includes(f)) : g.fields;
                      if (offered.length === 0) return null;
                      return (
                        <optgroup key={g.group} label={t(g.labelKey)}>
                          {offered.map((f) => (
                            <option key={f} value={f}>
                              {t('clause.field.option', { field: f, kind: t(`fieldkind.${FIELD_KINDS[f]}.label`) })}
                            </option>
                          ))}
                        </optgroup>
                      );
                    })}
                    {(() => {
                      // Wave 6 — the set's custom fields (their kind in the label), offered where the
                      // row's sources send their feeder row; a name nothing resolves any more (an
                      // unbound field) stays as the row's own value rather than rendering blank.
                      const names = custom.map((f) => f.name as FieldName).filter((f) => !isStandardField(f));
                      const offered = names.filter((f) => f === field || !allowedFields || allowedFields.includes(f));
                      const orphan = !isStandardField(field) && !names.includes(field);
                      if (offered.length === 0 && !orphan) return null;
                      return (
                        <optgroup label={t('group.custom.label')}>
                          {offered.map((f) => (
                            <option key={f} value={f}>
                              {t('clause.field.option', { field: f, kind: t(`fieldkind.${kindOf(f)}.label`) })}
                            </option>
                          ))}
                          {orphan && <option value={field}>{field}</option>}
                        </optgroup>
                      );
                    })()}
                  </select>
                </span>

                <span className="pillwrap">
                  <span className="pillcap">{t('form.cap.compare')}</span>
                  <select
                    className="pill op"
                    aria-label={t('clause.row.operator', { n: i + 1 })}
                    disabled={readOnly}
                    value={clause.op}
                    onChange={(e) => changeOp(i, e.target.value as ClauseOp)}
                  >
                    {OPS.filter((o) => opsFor(kind).includes(o.op)).map((o) => (
                      <option key={o.op} value={o.op}>
                        {opLabel(o.op)}
                      </option>
                    ))}
                  </select>
                </span>

                <span className="pillwrap">
                  <span className="pillcap">{t('form.cap.value')}</span>
                  <span className="pill val">
                    {inputKind === 'boolean' && (
                      <select
                        className="f-val"
                        aria-label={t('clause.row.value', { n: i + 1 })}
                        data-testid={`${rowId}-value-boolean`}
                        disabled={readOnly}
                        value={clause.value === true ? 'true' : 'false'}
                        onChange={(e) => update(i, { value: e.target.value === 'true' })}
                      >
                        <option value="true">{t('block.true.label')}</option>
                        <option value="false">{t('block.false.label')}</option>
                      </select>
                    )}

                    {inputKind === 'number' && (
                      <input
                        type="number"
                        className="f-val num-input"
                        aria-label={t('clause.row.value', { n: i + 1 })}
                        data-testid={`${rowId}-value-number`}
                        disabled={readOnly}
                        value={typeof clause.value === 'number' ? clause.value : ''}
                        onChange={(e) => update(i, { value: e.target.value === '' ? '' : Number(e.target.value) })}
                      />
                    )}

                    {inputKind === 'text' && (
                      <input
                        type="text"
                        className="f-val"
                        aria-label={t('clause.row.value', { n: i + 1 })}
                        data-testid={`${rowId}-value-text`}
                        disabled={readOnly}
                        value={typeof clause.value === 'string' ? clause.value : ''}
                        onChange={(e) => update(i, { value: e.target.value })}
                      />
                    )}

                    {inputKind === 'list' && (
                      <ListInput
                        key={`${rowId}-${field}-${clause.op}`}
                        className="f-val"
                        aria-label={t('clause.row.valueList', { n: i + 1 })}
                        data-testid={`${rowId}-value-list`}
                        disabled={readOnly}
                        placeholder={kind === 'number' ? '5411, 5541, 4111' : '90, 80, 02'}
                        value={clause.value}
                        kind={kind}
                        onChange={(value) => update(i, { value })}
                      />
                    )}

                    {inputKind === 'set' &&
                      (() => {
                        const offered = masters ? masterListsFor(field, masters) : [];
                        const current = typeof clause.value === 'string' ? clause.value : '';
                        // The row's own list is always an option — a select whose value is not among
                        // its options renders blank; a list that no longer fits is flagged below the chain.
                        const foreign = current !== '' && !offered.some((l) => l.id === current);
                        return (
                          <>
                            <select
                              className="f-val"
                              aria-label={t('clause.row.valueSet', { n: i + 1 })}
                              data-testid={`${rowId}-value-set`}
                              disabled={readOnly}
                              value={current}
                              onChange={(e) => update(i, { value: e.target.value })}
                            >
                              {current === '' && <option value="">{t('clause.set.choose')}</option>}
                              {foreign && <option value={current}>{current}</option>}
                              {offered.map((l) => {
                                const option = t(l.itemCount === 1 ? 'clause.set.option.one' : 'clause.set.option.other', {
                                  id: l.id,
                                  name: l.name,
                                  n: num(l.itemCount),
                                });
                                // R11: `masterListsFor` offers lists bound to this field (by their
                                // template) and lists from before templates, which are marked.
                                return (
                                  <option key={l.id} value={l.id}>
                                    {l.templateId ? option : t('templates.clause.option.legacy', { option })}
                                  </option>
                                );
                              })}
                            </select>
                            {/* Nothing to choose: a known list set with none that fits, or no lists at hand and no list named yet. */}
                            {offered.length === 0 && (masters !== null || current === '') && (
                              <a
                                className="f-unit-sel set-empty"
                                style={{ display: 'grid', placeItems: 'center', color: 'var(--purple-deep)' }}
                                href={MASTERS_PATH}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`${rowId}-value-set-empty`}
                                title={t('clause.set.empty.tip')}
                              >
                                {t('clause.set.empty', { field })}
                              </a>
                            )}
                          </>
                        );
                      })()}

                    {inputKind === 'pair' && (
                      <span data-testid={`${rowId}-value-pair`} style={{ display: 'inline-flex', alignItems: 'stretch' }}>
                        <input
                          type="number"
                          className="f-val num-input"
                          style={{ width: 72 }}
                          aria-label={t('clause.row.min', { n: i + 1 })}
                          disabled={readOnly}
                          value={Array.isArray(clause.value) && typeof clause.value[0] === 'number' ? clause.value[0] : ''}
                          onChange={(e) => {
                            const max = Array.isArray(clause.value) ? clause.value[1] : 0;
                            update(i, { value: [e.target.value === '' ? '' : Number(e.target.value), max] });
                          }}
                        />
                        <span className="f-unit-sel" style={{ display: 'grid', placeItems: 'center' }}>
                          –
                        </span>
                        <input
                          type="number"
                          className="f-val num-input"
                          style={{ width: 72 }}
                          aria-label={t('clause.row.max', { n: i + 1 })}
                          disabled={readOnly}
                          value={Array.isArray(clause.value) && typeof clause.value[1] === 'number' ? clause.value[1] : ''}
                          onChange={(e) => {
                            const min = Array.isArray(clause.value) ? clause.value[0] : 0;
                            update(i, { value: [min, e.target.value === '' ? '' : Number(e.target.value)] });
                          }}
                        />
                      </span>
                    )}
                  </span>
                </span>

                {!readOnly && (
                  <button
                    type="button"
                    className="btn small ghost rowdel"
                    aria-label={t('clause.row.remove', { n: i + 1 })}
                    onClick={() => remove(i)}
                    title={t('clause.remove.tip')}
                  >
                    ✕
                  </button>
                )}
              </span>
            </Fragment>
          );
        })}

        {!readOnly && (
          <button type="button" className="pill add" onClick={add}>
            {t('clause.add.label')}
          </button>
        )}
      </div>

      {/* What is wrong with a row stays visible, under the chain. */}
      {clauses.map((_clause, i) => {
        const rowId = `${idPrefix}-${i}`;
        const problems = notes?.get(i) ?? [];
        return (
          <Fragment key={`${rowId}-notes`}>
            {problems.length > 0 && (
              <div className="why-missing" role="alert" data-testid={`${rowId}-problem`}>
                {t('clause.row.label', { n: i + 1 })} — {problems.join('; ')}
              </div>
            )}
          </Fragment>
        );
      })}

      {showPreview && (
        <div className="preview" style={{ marginTop: 10 }} data-testid={`${idPrefix}-preview`}>
          {formatClauses(clauses)}
        </div>
      )}
    </div>
  );
}
